/* eslint-disable no-mixed-spaces-and-tabs */
import React, { useMemo } from 'react';
import {
	TextField,
	FormLabel,
	FormGroup,
	Stack,
	FormHelperText,
	FormControl,
	Grid,
	Skeleton,
} from '@mui/material';
import '@/styles/fileuploader.scss';
import {
	GOALS_OF_COVER,
	SCOPE_OF_ADVICE,
	GOALS_OF_COVER_LABELS,
	CLIENT_TYPE,
	COMMERCIAL,
	DOMESTIC,
	NEEDS_ANALYSIS,
	FULL_NEEDS_ANALYSIS,
	ADDITIONAL_SPECIALIST_PRODUCTS,
	ADDITIONAL_GOALS_OF_COVER,
	OWNER_DOC_ID,
	ESTIMATED_CLOSE_DATE,
	DEAL_AMOUNT,
	CLIENT_QUOTED,
	BROKER_GROUP_ID,
} from '../validationSchema';
import { NeedsAnalysisSelect } from '@/components/selects/NeedsAnalysisSelect';
import FolioCheckbox from '@/components/FolioCheckbox';
import FolioRadioGroup from '@/components/FolioRadioGroup';
import { OwnerSelect } from '@/components/selects/OwnerSelect';
import { ASSIGNED_TO_HELP_TEXT } from '@/utils/helperText';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { NumericFormat } from 'react-number-format';
import { useSelector } from 'react-redux';
import { useGetUserDetailsQuery } from '@/features/user/userApi';
import { useFeatureFlagContext } from '@/context/featureFlagContext';
import { useLocation } from 'react-router-dom';
import RiskClassContext from '@/components/RiskClassContext';
import { useGetClassesOfRiskQuery } from '@/features/classesOfRisk/classesOfRiskApi';
import { useFormikContext } from 'formik';
import { BrokerGroupSelect } from '@/components/selects/BrokerGroupSelect';

export const AdditionalScopeFields = ({ formik, loading, disabled }) => {
	const { values, setFieldValue, setFieldTouched, getFieldProps, errors, touched, isSubmitting } =
		formik ?? useFormikContext();
	const account = useSelector((state) => state.msalAccount.account);
	const email = account?.username;
	const { data: userDetails } = useGetUserDetailsQuery(
		{ email },
		{
			skip: !email,
		},
	);
	const countryCode = userDetails?.broker?.country ?? 'NZ';
	const brokerGroupIds = userDetails?.brokerGroupArray;

	const { data: classesOfRisk, ...classesOfRiskQuery } = useGetClassesOfRiskQuery(
		{ countryCode, clientType: values[CLIENT_TYPE], brokerGroupIds },
		{ skip: userDetails?.isLoading || userDetails.isUninitialized },
	);

	const items = (classesOfRisk ?? []).filter((v) =>
		values[SCOPE_OF_ADVICE].includes(v.classOfRisk),
	);

	const handleRiskClassChange = (newItems) => {
		const needsAnalysis = (values[NEEDS_ANALYSIS] ?? []).slice();
		console.log('Current needsAnalysis', { needsAnalysis, newItems });
		const entries = Array.from(
			new Set(Object.keys(needsAnalysis).concat(newItems.map((v) => v.classOfRisk))),
		);

		const vals = entries.reduce((acc, key) => {
			if (Object.keys(needsAnalysis).includes(key)) {
				const newVal = needsAnalysis.find((k) => k.key === key);
				if (newVal) {
					acc[key] = newVal;
				}
			} else {
				acc[key] = CLIENT_QUOTED;
			}
			return acc;
		}, {});

		const needsAnalysisArray = Object.entries(vals).map(([key, value]) => ({ key, value }));

		console.log('Needs analysis array', needsAnalysisArray);

		setFieldValue(
			SCOPE_OF_ADVICE,
			newItems.map((v) => (typeof v === 'string' ? v : v.classOfRisk)),
		);
		setFieldValue(NEEDS_ANALYSIS, needsAnalysisArray);
		setTimeout(() => {
			setFieldTouched(SCOPE_OF_ADVICE, true, false);
			setFieldTouched(NEEDS_ANALYSIS, true, false);
		}, 10);
	};

	const { getFeatureFlags } = useFeatureFlagContext();
	const showNewBusinessKPI = Boolean(getFeatureFlags('newBusinessKPI')?.active);
	const isClassOfRiskRequired = Boolean(getFeatureFlags('riskInfoRequired')?.active);
	const isDealAmountRequired = Boolean(getFeatureFlags('dealAmountRequired')?.active);

	const location = useLocation();
	const pathname = location?.pathname || '';
	const isRenewalForm = pathname.includes('create-renewal');

	const scopeOfAdviceArray = useMemo(
		() => values[SCOPE_OF_ADVICE] ?? [],
		[values[SCOPE_OF_ADVICE]],
	);

	const handleAnalysisChange = (name, value) => {
		setFieldValue(name, value);
		const currentAnalysis = values[NEEDS_ANALYSIS] || [];
		const updatedAnalysis = currentAnalysis.filter((item) => item.key !== name);
		updatedAnalysis.push({ key: name, value: value });
		console.log('updating array: ', updatedAnalysis);
		setFieldValue(NEEDS_ANALYSIS, updatedAnalysis);
	};

	return (
		<Stack spacing={4}>
			<Grid container rowSpacing={4} columnSpacing={2}>
				<Grid size={12}>
					<FormLabel>{'Goals of Cover'}</FormLabel>
					<FormGroup row sx={{ width: '100%' }}>
						<Grid container sx={{ width: '100%' }} rowSpacing={1} columnSpacing={2}>
							{Object.entries(GOALS_OF_COVER_LABELS).map((entry) => {
								const [value, label] = entry;
								return (
									<Grid size={{ xs: 6, sm: 4 }} key={`goals-of-cover-${value}`}>
										<FolioCheckbox
											checked={(values[GOALS_OF_COVER] ?? []).includes(value)}
											label={label}
											onChange={() => {
												const valuesArray = values[GOALS_OF_COVER] ?? [];
												const newValues = valuesArray.includes(value)
													? valuesArray.filter((i) => i !== value)
													: [...valuesArray, value];
												setFieldValue(GOALS_OF_COVER, newValues);
											}}
											disabled={loading}
										/>
									</Grid>
								);
							})}
						</Grid>
					</FormGroup>
				</Grid>
				<Grid size={12}>
					<FormLabel>{'Additional goals of cover'}</FormLabel>
					<TextField
						fullWidth
						name={ADDITIONAL_GOALS_OF_COVER}
						multiline
						rows={4}
						{...getFieldProps(ADDITIONAL_GOALS_OF_COVER)}
						disabled={loading}
					/>
				</Grid>
				<Grid size={4}>
					<FormControl fullWidth>
						<FormLabel>{'Deal assignee'}</FormLabel>
						<OwnerSelect
							initialId={values[OWNER_DOC_ID]}
							disabled={isSubmitting}
							showGroup={true}
							onChange={(user) => {
								setFieldValue(OWNER_DOC_ID, user?.id);
							}}
						/>
						<FormHelperText sx={{ mt: 0.5, mx: 0 }}>
							{ASSIGNED_TO_HELP_TEXT}
						</FormHelperText>
					</FormControl>
				</Grid>
				<Grid size={8}>
					<FormLabel>{'Risk Type'}</FormLabel>
					<FolioRadioGroup
						name={CLIENT_TYPE}
						value={values[CLIENT_TYPE]}
						onChange={(e) => {
							setFieldValue(CLIENT_TYPE, e.target.value);
							setFieldValue(SCOPE_OF_ADVICE, []);
						}}
						disabled={disabled}
						items={[
							{ value: COMMERCIAL, label: 'Commercial' },
							{ value: DOMESTIC, label: 'Domestic' },
						]}
					/>
				</Grid>
				{showNewBusinessKPI && !isRenewalForm && (
					<>
						<Grid size={{ xs: 12, md: 6 }}>
							<FormControl fullWidth>
								<FormLabel required={isDealAmountRequired}>
									{'Deal amount'}
								</FormLabel>
								<NumericFormat
									prefix='$'
									decimalScale={2}
									fixedDecimalScale
									thousandSeparator
									allowNegative={false}
									customInput={TextField}
									value={values[DEAL_AMOUNT]}
									onValueChange={({ floatValue }) => {
										setFieldValue(DEAL_AMOUNT, floatValue ?? null);
									}}
									disabled={loading}
									name={DEAL_AMOUNT}
								/>
								{touched[DEAL_AMOUNT] && errors[DEAL_AMOUNT] && (
									<FormHelperText error sx={{ mt: 1 }}>
										{errors[DEAL_AMOUNT]}
									</FormHelperText>
								)}
							</FormControl>
						</Grid>
						<Grid size={{ xs: 12, md: 6 }}>
							<FormLabel>{'Estimated close date'}</FormLabel>
							<LocalizationProvider dateAdapter={AdapterDateFns}>
								<DatePicker
									name={ESTIMATED_CLOSE_DATE}
									sx={{ width: '100%' }}
									disabled={isSubmitting}
									format='dd/MM/yyyy'
									value={values[ESTIMATED_CLOSE_DATE]}
									onChange={(value) => setFieldValue(ESTIMATED_CLOSE_DATE, value)}
								/>
							</LocalizationProvider>
						</Grid>
					</>
				)}
				<Grid size={12}>
					<FormLabel
						htmlFor='risk-Classes'
						required={isClassOfRiskRequired && !isRenewalForm}
					>
						{'Risk classes considered'}
					</FormLabel>
					<RiskClassContext
						{...{
							items,
							setItems: (v) => handleRiskClassChange(v),
							clientType: values[CLIENT_TYPE],
							disabled,
						}}
					/>
					{touched[SCOPE_OF_ADVICE] && errors[SCOPE_OF_ADVICE] && (
						<FormHelperText error sx={{ mt: 1 }}>
							{errors[SCOPE_OF_ADVICE]}
						</FormHelperText>
					)}
				</Grid>
			</Grid>

			<Grid container rowSpacing={1} columnSpacing={2}>
				{classesOfRiskQuery.isLoading || classesOfRiskQuery.isUninitialized ? (
					<Grid size={{ xs: 12 }}>
						<Skeleton width='100%' />
					</Grid>
				) : scopeOfAdviceArray.includes(FULL_NEEDS_ANALYSIS) ? (
					(classesOfRisk ?? []).map((item, index) => (
						<Grid size={6} key={`needs-scope-selection-${item.classOfRisk}-${index}`}>
							<NeedsAnalysisSelect
								classOfRisk={item.classOfRisk}
								label={item.label}
								analysisValue={
									(values[NEEDS_ANALYSIS] ?? []).find(
										(v) => v.key == item.classOfRisk,
									)?.value
								}
								disabled={disabled}
								setAnalysisValue={(val) =>
									handleAnalysisChange(item.classOfRisk, val)
								}
							/>
						</Grid>
					))
				) : (
					scopeOfAdviceArray.map((value, index) => {
						const val =
							typeof value === 'string'
								? classesOfRisk.find((c) => c.classOfRisk === value)
								: classesOfRisk.find((c) => c.classOfRisk === value?.classOfRisk);
						return (
							<Grid
								size={6}
								key={`needs-scope-selection-${val.classOfRisk}-${index}`}
							>
								<NeedsAnalysisSelect
									classOfRisk={val.classOfRisk}
									analysisValue={
										(values[NEEDS_ANALYSIS] ?? []).find(
											(v) => v.key == val.classOfRisk,
										)?.value
									}
									disabled={disabled}
									setAnalysisValue={(v) =>
										handleAnalysisChange(val.classOfRisk, v)
									}
									label={val.label}
								/>
							</Grid>
						);
					})
				)}
			</Grid>
			<FormControl fullWidth>
				<FormLabel>
					{'Additional specialist insurance products discussed and the outcomes'}
				</FormLabel>
				<TextField
					fullWidth
					name={ADDITIONAL_SPECIALIST_PRODUCTS}
					multiline
					rows={4}
					{...getFieldProps(ADDITIONAL_SPECIALIST_PRODUCTS)}
					disabled={loading}
				/>
			</FormControl>
		</Stack>
	);
};
